import {
  Badge,
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  MenuProps,
  message,
  Row,
  Select,
  UploadFile,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  DeleteOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled,
  HomeOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import UsersForm, { UserFormValues } from "../Form";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import {
  UserCreateParams,
  UserUpdateParams,
  useUserDelete,
  useUserGetById,
  useUserResendLink,
  useUserUpdate,
} from "../../../../hooks/api/users";
import { useLocation, useNavigate, useParams } from "react-router";
import { useAuth, User, UserStatusType } from "../../../../store/auth";
import { generateFileObjFromUrl } from "../../../Common/ImageUpload";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { useSimpleModal } from "../../../Common/Modal/Simple";
import { Role, UserStatusDisplay } from "../../../../enums/user";
import { enumToOptionsArray } from "../../../../utils";
import { getUserStatusColor } from "..";
import Loading from "../../../Common/Loading";

const initialState: UserFormValues = {
  // fullName: "",
  email: "",
  firstName: "",
  lastName: "",
  // avatar: "",
  role: Role.ADMIN,
  plan: "",
  birthdate: null,
  gender: "",
  address: "",
  country: "",
  city: "",
  zipCode: "",
  phone: "",
};

const prepareValues = (data: User): UserCreateParams => ({
  ...initialState,
  ...data,
});

export default function UsersEditContent(): React.ReactNode | null {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState(initialState);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { open, contextHolder } = useSimpleModal();
  const userById = useUserGetById(id);
  const userUpdate = useUserUpdate();
  const userDelete = useUserDelete(id);
  const userResendLink = useUserResendLink();
  const location = useLocation();
  const fromPage = location.state?.fromPage || "1";

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Managers", href: "/users" },
      { title: t("common.edit") },
    ]);
  }, [i18n.language, t]);

  useEffect(() => {
    if (userById.data && !userById.loading && !userById.error) {
      const values = prepareValues(userById.data);
      setInitialValues(values);
      form.setFieldsValue(values);
    }
  }, [userById.data, userById.loading, userById.error]);

  useEffect(() => {
    if (userUpdate.data) {
      navigate(`/users?current=${fromPage}`);
    }
  }, [userUpdate, fromPage, navigate]);

  useEffect(() => {
    if (!userDelete.error && !userDelete.loading && userDelete.data) {
      navigate(`/users?current=${fromPage}`);
    }
  }, [
    userDelete.error,
    userDelete.loading,
    userDelete.data,
    fromPage,
    navigate,
  ]);

  const handleSubmit = async (values: UserUpdateParams) => {
    form.validateFields().then(() => {
      const payload: UserUpdateParams = {
        ...values,
        // avatar: fileList.length > 0 ? fileList[0].url : "",
        // zipCode: values.zipCode ?? "",
      };
      userUpdate.fetch({ ...payload }, id);
    });
  };

  useMessageSuccess([userUpdate], "Changes saved");
  useMessageSuccess([userDelete], "Manager deleted");
  useMessageError([userUpdate]);
  useMessageError([userResendLink]);
  useMessageSuccess([userResendLink], "Success");

  return (
    <SinglePageContainer
      title="Edit manager"
      extra={
        <>
          {userById.data?.status === "pending" ? (
            <Button
              onClick={() => {
                userResendLink.fetch(undefined, `${id}/resend-link`);
              }}
            >
              Resend invitation
            </Button>
          ) : null}
          <Button type="primary" onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
          {userById.data?.email !== user?.email ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                open({
                  icon: <ExclamationCircleFilled />,
                  title: t("pages.users.deleteUserTitle"),
                  content: (
                    <span>
                      {t("pages.users.deleteUser")}{" "}
                      <b>{userById.data?.firstName || "-"}</b>?
                    </span>
                  ),
                  cancelText: t("common.cancel"),
                  okText: t("common.delete"),
                  okButtonProps: { danger: true },
                  onOk: () => userDelete.fetch(),
                });
              }}
            />
          ) : null}
        </>
      }
    >
      <div>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleSubmit}
        >
          <UsersForm fileList={fileList} setFileList={setFileList} />
          <Row gutter={18}>
            <Col span={12}>
              <Form.Item label="Status" name="status">
                <Select
                  placeholder="Select"
                  options={enumToOptionsArray(UserStatusDisplay)}
                  optionRender={(option) => {
                    const status = option.value as UserStatusType | undefined;

                    if (!status) return option.label;

                    return (
                      <Badge
                        color={getUserStatusColor(status)}
                        text={
                          <span className="capitalize-first-letter">
                            {option.label}
                          </span>
                        }
                      />
                    );
                  }}
                  labelRender={(label) => {
                    const status = label.value as UserStatusType | undefined;

                    if (!status) return label.label;

                    return (
                      <Badge
                        color={getUserStatusColor(status)}
                        text={
                          <span className="capitalize-first-letter">
                            {label.label}
                          </span>
                        }
                      />
                    );
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Created at">
                <Input
                  disabled
                  style={{ color: "#000000E0" }}
                  value={
                    userById?.data?.createdAt
                      ? dayjs(userById.data.createdAt).format(
                        "YYYY-MM-DD HH:mm",
                      )
                      : "-"
                  }
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Last log in">
                <Input
                  disabled
                  style={{ color: "#000000E0" }}
                  value={
                    userById?.data?.lastLoginAt
                      ? dayjs(userById.data.lastLoginAt).format(
                        "YYYY-MM-DD HH:mm",
                      )
                      : "-"
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      {contextHolder}
      {userById.loading || userUpdate.loading ? <Loading absolute /> : null}
    </SinglePageContainer>
  );
}
